import * as React from "react";
import { Callout, FontIcon, Link, mergeStyles, mergeStyleSets, Stack, Text, TooltipHost } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { StatusType } from "../../helpers/statusHelper";

interface StatusInfo {
  title?: string;
  description: string
}
export interface StatusProps {
  /**
   *Status type to be used for the Status
   */
  status: StatusType;
  /**
   * Label for the Status
   */
  label?: string;
  /**
   * Tooltip will be shown when hover over an icon
   */
  tooltip?: string;
  /**
   * Icon position.
   * @default: left
   */
  iconPosition?: "left" | "right"
  /**
   * Providing status info will show the @field label (required if status info is passed) as hyperlink.
   * @field title or @field description will be shown on the callout.
   */
  statusInfo?: StatusInfo;
}

const iconClass = mergeStyles({
  height: 18,
  width: 18,
  cursor: "default",
});

const styles = mergeStyleSets({
  callout: {
    width: 360,
    padding: "18px",
  },
});

function getStatusColorAndIcon(type: StatusType): { [key: string]: string } {
  switch (type) {
    case StatusType.INFO:
      return { color: "#0451d5", name: "InfoSolid" };
    case StatusType.ERROR:
      return { color: "#db0e1c", name: "StatusErrorFull" };
    case StatusType.SUCCESS:
      return { color: "#53aa00", name: "CompletedSolid" };
    case StatusType.NONE:
      return { color: "#6f6f6f", name: "CircleFill" };
    case StatusType.PENDING:
      return { color: "#0451d5", name: "SyncStatusSolid" };
    case StatusType.STOPPED:
      return { color: "#6f6f6f", name: "CircleStopSolid" };
    case StatusType.CANCELLED:
      return { color: "#6f6f6f", name: "StatusErrorFull" };
    case StatusType.DISABLED:
      return { color: "#db0e1c", name: "DRM" };
    case StatusType.UNKNOWN:
      return { color: "#6f6f6f", name: "UnknownSolid" };
    case StatusType.WARNING:
      return { color: "#d66b00", name: "WarningSolid" };
    default: return {};
  }
}

function getIcon(status: StatusType, iconTooltip: string): JSX.Element {
  const { color, name } = getStatusColorAndIcon(status);
  return (
    <>
      {iconTooltip && (
        <span style={{ marginTop: "2px" }}>
          <TooltipHost
            content={iconTooltip}
          >
            <FontIcon
              iconName={name}
              className={mergeStyles(iconClass, { color })}
            />
          </TooltipHost>
        </span>
      )}
      {!iconTooltip
        && (
        <span style={{ marginTop: "2px" }}>
          <FontIcon
            iconName={name}
            className={mergeStyles(iconClass, { color })}
          />
        </span>
        )}
    </>
  );
}

/**
 * The Status component render the status icon for the provided status type with
 * a tooltip on hovering over it
 */
export const Status = ({
  status,
  label,
  tooltip = "",
  statusInfo,
  iconPosition = "left",
}: StatusProps): JSX.Element => {
  const [openInfoCallout, setOpenInfoCallout] = React.useState<boolean>(false);
  const targetId = useId("status-info-callout");
  return (
    <>
      <Stack
        tokens={{ childrenGap: 4 }}
        horizontal
        styles={{ root: { maxWidth: 200 } }}
      >
        {iconPosition === "left" && getIcon(status, tooltip)}
        {!statusInfo && (
        <Text aria-label={label}>
          {label}
        </Text>
        )}
        {statusInfo && label && (
        <Link
          id={targetId}
          onClick={() => setOpenInfoCallout(true)}
          aria-label={label}
        >
          {label}
        </Link>
        )}
        {iconPosition === "right" && getIcon(status, tooltip)}
      </Stack>
      {openInfoCallout && (
      <Callout
        isBeakVisible
        setInitialFocus
        target={`#${targetId}`}
        onDismiss={() => setOpenInfoCallout(false)}
        className={styles.callout}
      >
        {statusInfo?.title && (
        <Text
          block
          styles={{
            root: {
              fontSize: 24,
              fontWeight: 600,
              marginBottom: 10,
            },
          }}
        >
          {statusInfo?.title}
        </Text>
        )}
        <Text
          block
          styles={{
            root: {
              marginBottom: 18,
              fontStyle: "normal",
              overflowWrap: "break-word",
            },
          }}
        >
          {statusInfo?.description}
        </Text>
      </Callout>
      )}
    </>
  );
};
